import { useQuery } from '@tanstack/react-query';

import { addonList } from '@api/sdk.gen';
import { AddonLifecycleStatus, AddonsAddonListResponse } from '@api/types.gen';

import { withError } from '@/react-tools/react-query';

import { queryKeys } from '../query-keys';

const REFETCH_INTERVAL_WHILE_IN_PROGRESS = 10_000;

export function useAddons<T = AddonsAddonListResponse | undefined>({
  enabled = true,
  select,
}: {
  enabled?: boolean;
  select?: (data: AddonsAddonListResponse | undefined) => T;
} = {}) {
  return useQuery({
    queryKey: queryKeys.list(),
    queryFn: getAddons,
    enabled,
    select,
    // From the raw response, so a narrowing `select` can't hide in-progress states.
    refetchInterval: (_data, query) =>
      hasAddonInProgress(query.state.data)
        ? REFETCH_INTERVAL_WHILE_IN_PROGRESS
        : false,
    ...withError('Unable to load add-ons'),
  });
}

function hasAddonInProgress(data?: AddonsAddonListResponse) {
  return Boolean(
    data?.addons?.some(
      (addon) =>
        addon.lifecycleStatus ===
          AddonLifecycleStatus.ADDON_STATUS_INSTALLING ||
        addon.lifecycleStatus ===
          AddonLifecycleStatus.ADDON_STATUS_UNINSTALLING ||
        addon.lifecycleStatus === AddonLifecycleStatus.ADDON_STATUS_UPGRADING
    )
  );
}

async function getAddons() {
  const { data } = await addonList({});
  return data;
}
